import React from "react";

export function Sparkline({ data, stroke = "currentColor", fill = "none", strokeWidth = 1.25, height = 60, width = 240, baseline = false, dots = false }: any) {
  if (!data || data.length === 0) return null;
  const min = Math.min(...data), max = Math.max(...data);
  const range = max - min || 1;
  const pad = strokeWidth + 1;
  const w = width, h = height;
  const x = (i: number) => (i / (data.length - 1)) * (w - pad * 2) + pad;
  const y = (v: number) => h - pad - ((v - min) / range) * (h - pad * 2);
  const path = data.map((v: number, i: number) => `${i === 0 ? "M" : "L"}${x(i).toFixed(2)} ${y(v).toFixed(2)}`).join(" ");
  const areaPath = fill === "none" ? null : `${path} L${x(data.length - 1).toFixed(2)} ${h} L${x(0).toFixed(2)} ${h} Z`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width="100%" height={h} preserveAspectRatio="none" style={{ display: "block", overflow: "visible" }}>
      {baseline && <line x1={pad} x2={w - pad} y1={h - pad} y2={h - pad} stroke="currentColor" strokeWidth="0.5" opacity="0.25" />}
      {areaPath && <path d={areaPath} fill={fill} />}
      <path d={path} fill="none" stroke={stroke} strokeWidth={strokeWidth} strokeLinecap="square" strokeLinejoin="miter" />
      {dots && data.map((v: number, i: number) => i === data.length - 1 && <circle key={i} cx={x(i)} cy={y(v)} r={2.5} fill={stroke} />)}
    </svg>
  );
}

export function Bars({ data, height = 60, width = 260, color = "currentColor", zero = true, gap = 2 }: any) {
  const max = Math.max(...data.map(Math.abs)) || 1;
  const w = width, h = height;
  const n = data.length;
  const bw = (w - gap * (n - 1)) / n;
  const zeroY = h / 2;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width="100%" height={h} preserveAspectRatio="none" style={{ display: "block" }}>
      {zero && <line x1={0} x2={w} y1={zeroY} y2={zeroY} stroke="currentColor" opacity="0.25" strokeWidth="0.5" />}
      {data.map((v: number, i: number) => {
        const x0 = i * (bw + gap);
        const bh = (Math.abs(v) / max) * (h / 2 - 2);
        const y0 = v >= 0 ? zeroY - bh : zeroY;
        return <rect key={i} x={x0} y={y0} width={bw} height={Math.max(bh, 0.5)} fill={color} opacity={v >= 0 ? 1 : 0.45} />;
      })}
    </svg>
  );
}

export function Donut({ slices, size = 120, thickness = 14, gap = 4, trackColor = "rgba(255,255,255,0.08)" }: any) {
  const r = (size - thickness) / 2;
  const c = 2 * Math.PI * r;
  const total = slices.reduce((s: number, x: any) => s + x.value, 0);
  let acc = 0;
  return (
    <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} style={{ display: "block" }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={trackColor} strokeWidth={thickness} />
      {slices.map((s: any, i: number) => {
        const len = (s.value / total) * c;
        const dash = `${Math.max(len - gap, 0)} ${c}`;
        const offset = -acc;
        acc += len;
        return (
          <circle key={i} cx={size / 2} cy={size / 2} r={r} fill="none"
            stroke={s.color} strokeWidth={thickness} strokeDasharray={dash}
            strokeDashoffset={offset} transform={`rotate(-90 ${size / 2} ${size / 2})`} />
        );
      })}
    </svg>
  );
}

export function ImgPlaceholder({ label = "screenshot", width = "100%", height = 180, monoFont = "ui-monospace, JetBrains Mono, monospace", borderColor = "#2a2a2a", stripe = "rgba(255,255,255,0.04)" }: any) {
  return (
    <div style={{
      width, height,
      backgroundImage: `repeating-linear-gradient(135deg, transparent 0 10px, ${stripe} 10px 11px)`,
      border: `1px solid ${borderColor}`,
      display: "flex", alignItems: "center", justifyContent: "center",
      color: "rgba(255,255,255,0.45)", font: `11px ${monoFont}`, letterSpacing: "0.08em", textTransform: "uppercase",
    }}>
      {label}
    </div>
  );
}

export const Arrow = ({ dir = "up", size = 10 }: any) => {
  const paths: any = {
    up:   "M2 8 L6 3 L10 8",
    down: "M2 4 L6 9 L10 4",
    right:"M3 2 L8 6 L3 10",
  };
  return (
    <svg width={size} height={size} viewBox="0 0 12 12" style={{ display: "inline-block", verticalAlign: "middle" }}>
      <path d={paths[dir]} stroke="currentColor" strokeWidth="1.4" fill="none" strokeLinecap="square" />
    </svg>
  );
};
